import React from 'react';
import Typography from '@/components/ui/typography';
import { SvgIcon } from '@/components/ui/SvgIcon';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '../ui';
import { Button } from '@/components/ui/button';
import { MoreVertical, Edit, Trash2, ClipboardPlus } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TeamMember } from '@/types/api';
import { usePermissions } from '@/hooks/usePermissions';

const formatLastActivity = (dateString: string | null): string => {
  if (!dateString) {
    return 'Never';
  }

  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${month}-${day}-${year} ${hours}:${minutes}`;
};

const formatUserName = (firstName?: string, lastName?: string): string => {
  const hasFirstName = firstName && firstName.trim() !== '';
  const hasLastName = lastName && lastName.trim() !== '';

  if (hasFirstName && hasLastName) {
    return `${firstName} ${lastName}`;
  } else if (hasFirstName) {
    return firstName;
  } else if (hasLastName) {
    return lastName;
  } else {
    return 'First and Last Name';
  }
};

interface UserCardProps {
  user: TeamMember;
  ehrUsernameLabel: string;
  onEdit?: (user: TeamMember) => void;
  onDelete?: (user: TeamMember) => void;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  ehrUsernameLabel,
  onEdit,
  onDelete,
}) => {
  const { hasPermission } = usePermissions();

  // Only show dropdown menu if user has write-user or delete-user permissions
  const canEdit = hasPermission('write-user') && onEdit;
  const canDelete = hasPermission('delete-user') && onDelete;
  const hasActions = canEdit || canDelete;

  const displayName = formatUserName(user.firstName, user.lastName);

  return (
    <TooltipProvider>
      <Card className="rounded-xl border border-gray-200 p-2 sm:p-3 md:p-4 hover:border-[#5536F5]/30 hover:shadow-md">
        <CardContent className="p-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
              <div className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 flex items-center justify-center flex-shrink-0">
                <SvgIcon
                  type="inviteUser"
                  color="primary"
                  className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5"
                />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Typography
                    variant="h4"
                    weight="normal"
                    className="text-neutral-900 text-xs sm:text-sm md:text-base truncate min-w-0 cursor-help"
                  >
                    {displayName}
                  </Typography>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs sm:text-sm">{displayName}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Only show dropdown menu if user has appropriate permissions */}
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 sm:h-8 sm:w-8 p-0 hover:bg-[#5536F5]/5 hover:text-[#5536F5] flex-shrink-0"
                  >
                    <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32 sm:w-40">
                  {canEdit && (
                    <DropdownMenuItem
                      onClick={() => onEdit(user)}
                      className="cursor-pointer text-xs sm:text-sm hover:bg-[#5536F5]/5"
                    >
                      <Edit className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      Edit user
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem
                      onClick={() => onDelete(user)}
                      className="cursor-pointer text-red-600 focus:text-red-600 text-xs sm:text-sm hover:bg-red-50"
                      variant="destructive"
                    >
                      <Trash2 className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      Delete user
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <Separator className="my-2 sm:my-3 md:my-4 bg-[#5536F5]/10" />

          <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
              <Typography
                variant="small"
                className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
              >
                Role
              </Typography>
              <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                <SvgIcon
                  type="inviteRole"
                  color="primary"
                  className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Typography
                      variant="small"
                      className="text-neutral-700 truncate text-xs sm:text-sm cursor-help min-w-0"
                    >
                      {user.role}
                    </Typography>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs sm:text-sm">{user.role}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
              <Typography
                variant="small"
                className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
              >
                Email
              </Typography>
              <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                <SvgIcon
                  type="inviteEmail"
                  color="primary"
                  className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="min-w-0 flex-1">
                      <Typography
                        variant="small"
                        className="text-neutral-700 line-clamp-2 cursor-help text-xs sm:text-sm"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.email}
                      </Typography>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs break-words text-xs sm:text-sm">
                      {user.email}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {user.ehrUsername && (
              <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
                <Typography
                  variant="small"
                  className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
                >
                  {ehrUsernameLabel}
                </Typography>
                <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                  <ClipboardPlus className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0 text-[#5536F5]" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Typography
                        variant="small"
                        className="text-neutral-700 truncate text-xs sm:text-sm cursor-help min-w-0"
                      >
                        {user.ehrUsername}
                      </Typography>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs sm:text-sm">{user.ehrUsername}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
              <Typography
                variant="small"
                className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
              >
                Last Activity
              </Typography>
              <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                <SvgIcon
                  type="lastActivity"
                  color="primary"
                  className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Typography
                      variant="small"
                      className="text-neutral-700 truncate text-xs sm:text-sm cursor-help min-w-0"
                    >
                      {formatLastActivity(user.lastLogin)}
                    </Typography>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs sm:text-sm">
                      {formatLastActivity(user.lastLogin)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default UserCard;
